import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { updateSelectedSchool } from '../dashboard/StaticSidebar';

export default function TodaysAttendanceTable({ attendanceData }) {
  const navigate = useNavigate();
  const { currentOrganization, currentSchool, availableSchools, setCurrentSchool } = useTenant();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'present', 'absent', 'late', 'excused'
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;

  // Handle navigation to student tracking page
  const handleTrackStudent = (student) => {
    const studentId = student.id || student.student_id || student.STUDENT_UNIQUE_ID || student.student_unique_id;
    
    // Find the school by matching the student's school_id with available schools
    let schoolToUse = currentSchool; // Use current school context if available
    
    // If student has a school_id and it's different from current school, find the correct school
    if (student.school_id && availableSchools.length > 0) {
      const foundSchool = availableSchools.find(s => 
        s.id === student.school_id || 
        s.unique_id === student.school_id || 
        s.unique_id === student.school_id
      );
      if (foundSchool) {
        schoolToUse = foundSchool;
      }
    }
    
    // Get the school's unique_id for URL routing
    const schoolUniqueId = schoolToUse?.unique_id || schoolToUse?.unique_id;
    
    if (!studentId || !schoolUniqueId || !currentOrganization) {
      console.error('Missing required data for navigation:', { 
        studentId, 
        schoolUniqueId, 
        currentOrganization: !!currentOrganization,
        student,
        schoolToUse
      });
      return;
    }
    
    // Switch to the student's school if it's different from current school
    if (schoolToUse && (!currentSchool || currentSchool.id !== schoolToUse.id)) {
      console.log('🔄 Switching school context for student tracking:', {
        from: currentSchool?.name || 'none',
        to: schoolToUse.name
      });
      setCurrentSchool(schoolToUse);
      updateSelectedSchool(schoolToUse);
    }
    
    // Navigate to student tracking page with proper URL structure (same as StudentManagementPage)
    const trackingUrl = `/org/${currentOrganization.unique_id}/school/${schoolUniqueId}/student-tracking/${studentId}`;
    console.log('Navigating to student tracking:', { 
      trackingUrl, 
      student, 
      schoolToUse,
      studentId
    });
    
    navigate(trackingUrl, { 
      state: { 
        selectedStudent: student,
        fromSchoolOverview: true
      } 
    });
  };

  // Flatten all students with their status
  const allStudents = useMemo(() => {
    console.log('TodaysAttendanceTable - Raw attendanceData:', attendanceData);
    
    // Use the same data path as SchoolAnalytics component
    if (!attendanceData?.dailyAnalytics?.[0]) {
      console.log('TodaysAttendanceTable - No dailyAnalytics data found:', {
        hasAttendanceData: !!attendanceData,
        hasDailyAnalytics: !!attendanceData?.dailyAnalytics,
        dailyAnalyticsLength: attendanceData?.dailyAnalytics?.length || 0,
        keys: attendanceData ? Object.keys(attendanceData) : []
      });
      return [];
    }

    const todayData = attendanceData.dailyAnalytics[0]; // Most recent day's data
    console.log('TodaysAttendanceTable - Today data:', todayData);
    console.log('TodaysAttendanceTable - Available properties:', Object.keys(todayData));
    
    const students = [];

    // Add present students
    if (Array.isArray(todayData.presentStudents)) {
      console.log('TodaysAttendanceTable - Present students:', todayData.presentStudents.length);
      console.log('TodaysAttendanceTable - Sample present student:', todayData.presentStudents[0]);
      todayData.presentStudents.forEach(student => {
        students.push({
          ...student,
          status: 'present',
          statusColor: 'bg-green-100 text-green-800',
          statusIcon: '✓'
        });
      });
    }

    // Add late students
    if (Array.isArray(todayData.lateStudents)) {
      console.log('TodaysAttendanceTable - Late students:', todayData.lateStudents.length);
      todayData.lateStudents.forEach(student => {
        students.push({
          ...student,
          status: 'late',
          statusColor: 'bg-yellow-100 text-yellow-800',
          statusIcon: ''
        });
      });
    }

    // Add absent students
    if (Array.isArray(todayData.absentStudents)) {
      console.log('TodaysAttendanceTable - Absent students:', todayData.absentStudents.length);
      todayData.absentStudents.forEach(student => {
        students.push({
          ...student,
          status: 'absent',
          statusColor: 'bg-red-100 text-red-800',
          statusIcon: '✗'
        });
      });
    }

    // Add excused students
    if (Array.isArray(todayData.excusedStudents)) {
      console.log('TodaysAttendanceTable - Excused students:', todayData.excusedStudents.length);
      todayData.excusedStudents.forEach(student => {
        students.push({
          ...student,
          status: 'excused',
          statusColor: 'bg-blue-100 text-blue-800',
          statusIcon: '📋'
        });
      });
    }

    console.log('TodaysAttendanceTable - Final students array:', students.length, students);
    return students;
  }, [attendanceData]);

  // Filter students based on search term and status
  const filteredStudents = useMemo(() => {
    let filtered = allStudents;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(student => student.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(student => {
        // Handle different possible field name structures
        const firstName = student.first_name || student.firstName || student.name?.split(' ')[0] || '';
        const lastName = student.last_name || student.lastName || student.name?.split(' ')[1] || '';
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const email = (student.email || '').toLowerCase();
        const className = (student.class_name || student.classroom?.name || '').toLowerCase();
        
        return fullName.includes(search) || 
               email.includes(search) || 
               className.includes(search);
      });
    }

    return filtered;
  }, [allStudents, searchTerm, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const endIndex = Math.min(startIndex + studentsPerPage, filteredStudents.length);
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Status counts for quick stats
  const statusCounts = useMemo(() => {
    const counts = {
      all: allStudents.length,
      present: allStudents.filter(s => s.status === 'present').length,
      absent: allStudents.filter(s => s.status === 'absent').length,
      late: allStudents.filter(s => s.status === 'late').length,
      excused: allStudents.filter(s => s.status === 'excused').length
    };
    return counts;
  }, [allStudents]);

  if (!attendanceData || allStudents.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Student Attendance</h3>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-2"></div>
          <p className="text-gray-500">No attendance data available for today</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Today's Student Attendance</h3>
        <p className="text-gray-600 text-sm">
          View and search all students with their attendance status for today
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search students by name, email, or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: statusCounts.all, color: 'bg-gray-100 text-gray-800' },
            { key: 'present', label: 'Present', count: statusCounts.present, color: 'bg-green-100 text-green-800' },
            { key: 'late', label: 'Late', count: statusCounts.late, color: 'bg-yellow-100 text-yellow-800' },
            { key: 'absent', label: 'Absent', count: statusCounts.absent, color: 'bg-red-100 text-red-800' },
            { key: 'excused', label: 'Excused', count: statusCounts.excused, color: 'bg-blue-100 text-blue-800' }
          ].map(({ key, label, count, color }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === key 
                  ? `${color} ring-2 ring-offset-1 ring-gray-300` 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Track
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedStudents.map((student, index) => (
              <tr key={`${student.id || student.student_id}-${student.status}-${index}`} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {((student.first_name || student.firstName || student.name?.split(' ')[0] || '')?.[0] || '').toUpperCase()}
                          {((student.last_name || student.lastName || student.name?.split(' ')[1] || '')?.[0] || '').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {student.first_name || student.firstName || student.name?.split(' ')[0] || ''} {student.last_name || student.lastName || student.name?.split(' ')[1] || ''}
                      </div>
                      {student.email && (
                        <div className="text-sm text-gray-500">{student.email}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${student.statusColor}`}>
                    <span className="mr-1">{student.statusIcon}</span>
                    {student.status.charAt(0).toUpperCase() + student.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => handleTrackStudent(student)}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Track
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {endIndex} of {filteredStudents.length} students
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* No Results */}
      {filteredStudents.length === 0 && (searchTerm || statusFilter !== 'all') && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-2">🔍</div>
          <p className="text-gray-500">No students found matching your search criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}